
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from '@/hooks/use-toast';
import { Star, BookOpen, Award, Filter, Search, ThumbsDown, ChevronDown, LayoutGrid, Info, AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { genres } from '@/data/galleryData';
import GalleryItem from '@/components/GalleryItem';
import { fetchGalleryItems, GalleryItem as GalleryItemType } from '@/services/galleryService';

const GalleryPage = () => {
  const [activeTab, setActiveTab] = useState("browse");
  const [selectedGenre, setSelectedGenre] = useState("All Genres");
  const [sortOrder, setSortOrder] = useState("newest");
  const [galleryData, setGalleryData] = useState<GalleryItemType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const loadGalleryData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchGalleryItems();
        setGalleryData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch gallery data:', err);
        setError('Failed to load gallery items. Please try again later.');
        toast({
          title: "Error Loading Gallery",
          description: "Could not load gallery items. Please try refreshing.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadGalleryData();
  }, [toast]);

  // Filter gallery data based on selected genre and tab
  const getFilteredData = () => {
    let filtered = [...galleryData];
    
    if (selectedGenre !== "All Genres") {
      filtered = filtered.filter(item => item.genre === selectedGenre);
    }
    
    if (activeTab === "best") {
      filtered = filtered.filter(item => item.rating >= 7);
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (activeTab === "worst") {
      filtered = filtered.filter(item => item.rating <= 3);
      filtered.sort((a, b) => a.rating - b.rating);
    } else {
      // Default sorting for browse tab
      if (sortOrder === "newest") {
        filtered.sort((a, b) => new Date(b.submitted_date).getTime() - new Date(a.submitted_date).getTime());
      } else if (sortOrder === "highest") {
        filtered.sort((a, b) => b.rating - a.rating);
      } else if (sortOrder === "lowest") {
        filtered.sort((a, b) => a.rating - b.rating);
      }
    }
    
    return filtered;
  };

  const filteredData = getFilteredData();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center mb-8">
            <h1 className="text-3xl font-bold text-bookNavy mb-2">Book Cover Gallery</h1>
            <p className="text-bookGray text-center max-w-3xl">
              Browse through our collection of analyzed book covers. See what works, what doesn't, 
              and get inspiration for your own designs!
            </p>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-bookNavy" />
              <span className="ml-2 text-lg">Loading gallery...</span>
            </div>
          ) : error ? (
            <Alert variant="destructive" className="mb-8">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <>
              <Tabs defaultValue="browse" className="w-full" onValueChange={setActiveTab}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <TabsList className="bg-bookCream">
                    <TabsTrigger value="browse" className="data-[state=active]:bg-bookNavy data-[state=active]:text-white">
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      Browse All
                    </TabsTrigger>
                    <TabsTrigger value="best" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
                      <Award className="h-4 w-4 mr-2" />
                      Best Covers
                    </TabsTrigger>
                    <TabsTrigger value="worst" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                      <ThumbsDown className="h-4 w-4 mr-2" />
                      Needs Work
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={selectedGenre} onValueChange={setSelectedGenre}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Genre" />
                      </SelectTrigger>
                      <SelectContent>
                        {genres.map(genre => (
                          <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {activeTab === "browse" && (
                      <Select value={sortOrder} onValueChange={setSortOrder}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="highest">Highest Rated</SelectItem>
                          <SelectItem value="lowest">Lowest Rated</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <TabsContent value="browse" className="mt-0">
                  {filteredData.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredData.map(item => (
                        <GalleryItem 
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          author={item.author}
                          imageUrl={item.image_url}
                          genre={item.genre}
                          rating={item.rating}
                          summary={item.summary}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-gray-500">No covers found matching your criteria.</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="best" className="mt-0">
                  {filteredData.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredData.map(item => (
                        <GalleryItem 
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          author={item.author}
                          imageUrl={item.image_url}
                          genre={item.genre}
                          rating={item.rating}
                          summary={item.summary}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-gray-500">No top-rated covers found in this genre.</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="worst" className="mt-0">
                  {filteredData.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredData.map(item => (
                        <GalleryItem 
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          author={item.author}
                          imageUrl={item.image_url}
                          genre={item.genre}
                          rating={item.rating}
                          summary={item.summary}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-gray-500">No covers needing work found in this genre.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default GalleryPage;
